'use client'

import { ApiError } from '@/lib/api-client'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { authService, type BackendUser } from '../services/auth-service'
import { useAuthStore } from '../stores/auth-store'
import {
	useRegistrationStore,
	type RegistrationData,
	type RegistrationStep,
} from '../stores/registration-store'

interface SignupResponse {
	user: BackendUser
	accessToken: string
}

/**
 * Hook para manejar el wizard de registro paso a paso.
 *
 * Flujo:
 * 1. createAccount() - Crea la cuenta vía /api/auth/signup (Step 0). La sesión
 *    queda iniciada con cookies httpOnly y el email queda confirmado.
 * 2. completeProfile() - Envía datos del wizard al backend propio.
 *
 * @example
 * ```tsx
 * const {
 *   currentStep,
 *   createAccount,
 *   waitForEmailConfirmation,
 *   completeProfile,
 *   nextStep,
 *   prevStep,
 * } = useRegistrationWizard()
 * ```
 */
export function useRegistrationWizard() {
	const router = useRouter()

	const {
		currentStep,
		data,
		isLoading,
		error,
		isHydrated,
		setStep,
		nextStep,
		prevStep,
		updateData,
		setSupabaseId,
		setEmailConfirmed,
		setLoading,
		setError,
		clearError,
		reset,
	} = useRegistrationStore()

	const { setUser, setOnboardingCompleted } = useAuthStore()

	// La auth propia confirma el email en el signup: el polling es un vestigio
	// de Supabase que se conserva solo como API pública (siempre inactivo).
	const [isPolling, setIsPolling] = useState(false)

	/**
	 * Crear cuenta vía /api/auth/signup.
	 * Este es el primer paso del wizard. Deja la sesión iniciada (cookies).
	 */
	const createAccount = useCallback(
		async (email: string, password: string) => {
			setLoading(true)
			clearError()

			try {
				const res = await fetch('/api/auth/signup', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ email, password }),
				})
				const raw = (await res.json().catch(() => ({}))) as Partial<SignupResponse> & {
					message?: string
				}

				if (!res.ok || !raw.user) {
					const errorMessage = getErrorMessage(raw.message ?? 'Error al crear la cuenta')
					setError(errorMessage)
					return { success: false, error: errorMessage }
				}

				// Guardar datos en el store
				updateData({ email, password })
				setSupabaseId(raw.user.id)

				// La auth propia no requiere confirmación de email
				setEmailConfirmed(true)
				setStep(1)

				return { success: true, needsConfirmation: false }
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : 'Error inesperado'
				setError(errorMessage)
				return { success: false, error: errorMessage }
			} finally {
				setLoading(false)
			}
		},
		[updateData, setSupabaseId, setEmailConfirmed, setStep, setLoading, setError, clearError]
	)

	/**
	 * Reenviar email de confirmación.
	 * Con la auth propia el email queda confirmado al crear la cuenta: no-op.
	 */
	const resendConfirmationEmail = useCallback(async () => {
		if (!data.email) {
			setError('No hay email registrado')
			return { success: false }
		}

		setEmailConfirmed(true)
		return { success: true }
	}, [data.email, setEmailConfirmed, setError])

	/**
	 * Verificación de confirmación de email.
	 * Con la auth propia no hay paso de confirmación: marca confirmado y avanza.
	 */
	const startPollingForConfirmation = useCallback(() => {
		setIsPolling(false)
		setEmailConfirmed(true)
		setStep(1)
	}, [setEmailConfirmed, setStep])

	/**
	 * Detener polling (compatibilidad; el polling ya no existe).
	 */
	const stopPolling = useCallback(() => {
		setIsPolling(false)
	}, [])

	/**
	 * Verificar sesión actual (útil al cargar la página).
	 */
	const checkCurrentSession = useCallback(async () => {
		try {
			const me = await authService.getMe()

			setSupabaseId(me.id)
			updateData({ email: me.email })
			setEmailConfirmed(true)

			return { confirmed: true, user: { id: me.id, email: me.email } }
		} catch {
			return { confirmed: false, user: null }
		}
	}, [setSupabaseId, updateData, setEmailConfirmed])

	/**
	 * Completar perfil y enviar datos al backend.
	 * Este es el paso final del wizard.
	 */
	const completeProfile = useCallback(async () => {
		if (!data.supabaseId) {
			setError('No hay sesión activa')
			return { success: false, error: 'No hay sesión activa' }
		}

		setLoading(true)
		clearError()

		try {
			let backendUser: BackendUser
			try {
				backendUser = await authService.registerInBackend()
			} catch (registerError) {
				// El usuario ya existe en el backend (reintento del wizard) → obtener perfil existente
				if (registerError instanceof ApiError && registerError.statusCode === 409) {
					backendUser = await authService.getMe()
				} else {
					throw registerError
				}
			}

			// Marcar el perfil como completado en el backend propio
			const updatedUser = await authService.completeProfile({
				supabaseId: data.supabaseId,
				name: data.companyName || undefined,
			})

			const setupResponse = await fetch('/api/sinko/company/setup', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					companyName: data.companyName,
					businessSegment: data.businessSegment,
					employeeCount: data.employeeCount,
					country: data.country,
					revenueLevel: data.revenueLevel,
					grossMargin: data.grossMargin,
					hasDebt: data.hasDebt,
					salesChannel: data.salesChannel,
					website: data.website,
					instagram: data.instagram,
					facebook: data.facebook,
					tiktok: data.tiktok,
					linkedin: data.linkedin,
					otherChannel: data.otherChannel,
				}),
			})

			if (!setupResponse.ok) {
				const setupError = (await setupResponse.json().catch(() => ({}))) as {
					error?: string
				}
				console.error('[registration] Company setup failed:', setupError)
				throw new Error(setupError?.error ?? 'Error al crear la empresa. Intenta de nuevo.')
			}

			// Usar datos de updatedUser (post-completeProfile) para tener el perfil actualizado
			const finalUser = updatedUser ?? backendUser
			setUser({
				id: finalUser.id,
				email: finalUser.email,
				role: finalUser.role,
				profile: {
					firstName: finalUser.firstName,
					lastName: finalUser.lastName,
					profileName: finalUser.profileName,
					avatarUrl: finalUser.avatarUrl ?? undefined,
				},
				avatarUrl: finalUser.avatarUrl,
				createdAt: finalUser.createdAt,
			})

			reset()
			setOnboardingCompleted(true)

			router.push('/dashboard')
			router.refresh()

			return { success: true }
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Error al completar el perfil'
			setError(errorMessage)
			return { success: false, error: errorMessage }
		} finally {
			setLoading(false)
		}
	}, [data, setUser, setOnboardingCompleted, router, reset, setLoading, setError, clearError])

	/**
	 * Ir a un paso específico con validación.
	 */
	const goToStep = useCallback(
		(step: RegistrationStep) => {
			// No permitir saltar pasos sin completar
			if (step > currentStep + 1) {
				return
			}
			setStep(step)
		},
		[currentStep, setStep]
	)

	/**
	 * Actualizar datos del formulario actual.
	 */
	const updateStepData = useCallback(
		(stepData: Partial<RegistrationData>) => {
			updateData(stepData)
		},
		[updateData]
	)

	return {
		// Estado
		currentStep,
		data,
		isLoading,
		error,
		isHydrated,
		isPolling,

		// Acciones de navegación
		nextStep,
		prevStep,
		goToStep,

		// Acciones de registro
		createAccount,
		resendConfirmationEmail,
		startPollingForConfirmation,
		stopPolling,
		checkCurrentSession,
		completeProfile,

		// Acciones de datos
		updateStepData,

		// Acciones de error
		clearError,

		// Reset
		reset,
	}
}

/**
 * Mapear mensajes de error conocidos a español.
 * Las rutas /api/auth/* ya devuelven mensajes en español; esto conserva
 * el mapeo para mensajes legacy y deja pasar el resto tal cual.
 */
function getErrorMessage(error: string): string {
	const errorMessages: Record<string, string> = {
		'User already registered': 'Este email ya está registrado',
		'Invalid login credentials': 'Credenciales incorrectas',
		'Email not confirmed': 'Por favor confirma tu email primero',
		'Password should be at least 6 characters': 'La contraseña debe tener al menos 6 caracteres',
		'Unable to validate email address: invalid format': 'Formato de email inválido',
		'Signup is disabled': 'El registro está deshabilitado',
		'User already exists': 'El usuario ya existe',
	}

	return errorMessages[error] || error
}

export type UseRegistrationWizardReturn = ReturnType<typeof useRegistrationWizard>
